import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Star as StarIcon
} from '@mui/icons-material';
// Firebase imports removed - using SQL API + Image Service now
import { ImageUploadService } from '../../accounting/inventory/services/imageUploadService';
import { getReferenceSettings, saveReferenceSettings, addReferenceItem, updateReferenceItem, deleteReferenceItem } from '../../../services/ecommerceApi';
import { useTranslation } from 'react-i18next';
import type { CorporateHeroSectionSettings } from '../../../types/corporateHero';
import { defaultCorporateHeroSectionSettings } from '../../../types/corporateHero';
import { buildCorporateHeroSavePayload, normalizeCorporateHeroSection } from '../../../utils/corporateHeroSettings';
import CorporateHeroSettingsPanel from './corporateHero/CorporateHeroSettingsPanel';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';
import {
  defaultReferenceDisplaySettings,
  embedReferenceDisplayInHeroJson,
  extractReferenceDisplayExtras,
  mergeReferenceDisplaySettings,
  type ReferenceCardStyle,
  type ReferenceDisplaySettings,
  type ReferenceLogoColor,
  type ReferenceLogoSize,
} from '../utils/referenceDisplaySettings';

interface Reference {
  id: string;
  companyName: string;
  logoUrl?: string;
  websiteUrl?: string;
  description?: string;
  category?: string;
  active: boolean;
}

interface ReferencesSettings {
  heroSection: CorporateHeroSectionSettings;
  references: Reference[];
  displaySettings: ReferenceDisplaySettings;
}

const defaultReferencesSettings: ReferencesSettings = {
  heroSection: {
    ...defaultCorporateHeroSectionSettings,
    title: 'Our References',
    description: 'Our trusted business partners and institutions we work with.',
    backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    height: 400,
    templateType: 'three-column-stats',
  },
  references: [],
  displaySettings: defaultReferenceDisplaySettings,
};

const CARD_STYLE_OPTIONS: Array<{ id: ReferenceCardStyle; titleKey: string; hintKey: string }> = [
  { id: 'elevated', titleKey: 'options.cardElevated', hintKey: 'options.cardElevatedHint' },
  { id: 'outlined', titleKey: 'options.cardOutlined', hintKey: 'options.cardOutlinedHint' },
  { id: 'glass', titleKey: 'options.cardGlass', hintKey: 'options.cardGlassHint' },
  { id: 'soft', titleKey: 'options.cardSoft', hintKey: 'options.cardSoftHint' },
  { id: 'minimal', titleKey: 'options.cardMinimal', hintKey: 'options.cardMinimalHint' },
];

const LOGO_SIZE_OPTIONS: Array<{ id: ReferenceLogoSize; titleKey: string }> = [
  { id: 'small', titleKey: 'options.logoSizeSmall' },
  { id: 'medium', titleKey: 'options.logoSizeMedium' },
  { id: 'large', titleKey: 'options.logoSizeLarge' },
  { id: 'xlarge', titleKey: 'options.logoSizeXlarge' },
];

const LOGO_COLOR_OPTIONS: Array<{ id: ReferenceLogoColor; titleKey: string; hintKey: string }> = [
  { id: 'color', titleKey: 'options.logoColorFull', hintKey: 'options.logoColorFullHint' },
  { id: 'grayscale', titleKey: 'options.logoColorGray', hintKey: 'options.logoColorGrayHint' },
];

interface ReferencesSettingsComponentProps {
  companyId: string | null;
  configId: string; // ✅ SQL için configId eklendi
  settings?: ReferencesSettings | null;
  onSettingsChange?: (settings: ReferencesSettings) => void;
}

const ReferencesSettingsComponent: React.FC<ReferencesSettingsComponentProps> = ({ 
  companyId,
  configId, // ✅ SQL için configId
  settings: externalSettings, 
  onSettingsChange 
}) => {
  const { t } = useTranslation('salesSystem');
  const [settings, setSettings] = useState<ReferencesSettings>(defaultReferencesSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReference, setEditingReference] = useState<Reference | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [referenceConfigId, setReferenceConfigId] = useState<string | null>(null); // SQL ReferenceConfig ID
  
  // Auto-save için debounce timer
  const autoSaveTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Ayarları yükle (SQL'den)
  useEffect(() => {
    const loadSettings = async () => {
      if (!companyId || !configId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('🔄 [REFERENCES] SQL den yükleniyor, ConfigId:', configId);
        
        const response = await getReferenceSettings(companyId, configId); // ✅ companyId eklendi
        
        if (response && response.success && response.data) {
          const data = response.data;
          
          // ReferenceConfig ID'yi sakla
          setReferenceConfigId(data.id);
          
          // Settings'i oluştur
          const loadedSettings: ReferencesSettings = {
            heroSection: normalizeCorporateHeroSection(data, {
              title: data.heroTitle || defaultReferencesSettings.heroSection.title,
              description: data.heroDescription || defaultReferencesSettings.heroSection.description,
              backgroundColor: data.heroBackgroundColor || defaultReferencesSettings.heroSection.backgroundColor,
              height: data.heroHeight || defaultReferencesSettings.heroSection.height,
            }),
            references: data.references || [],
            displaySettings: mergeReferenceDisplaySettings(data),
          };
          
          setSettings(loadedSettings);
          const extras = extractReferenceDisplayExtras(data.heroSettingsJson);
          if (extras.cardStyle == null && extras.logoSize == null) {
            // Eski kayıtlara yeni varsayılan tasarımı bir kez yaz
            debouncedAutoSave(loadedSettings);
          }
          console.log('✅ [REFERENCES] SQL den yükleme başarılı, Referans sayısı:', data.references?.length || 0);
        } else {
          // Veri yoksa default kullan
          console.log('ℹ️ [REFERENCES] Veri bulunamadı, default ayarlar kullanılıyor');
          setSettings(defaultReferencesSettings);
        }
        
      } catch (err) {
        console.error('❌ [REFERENCES] Ayarlar yüklenirken hata:', err);
        setError('An error occurred while loading settings');
        setSettings(defaultReferencesSettings);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [companyId, configId]); // ✅ externalSettings ve onSettingsChange kaldırıldı (sonsuz döngü önlemi)

  const buildSavePayload = (settingsToSave: ReferencesSettings) => {
    const heroPayload = buildCorporateHeroSavePayload(settingsToSave.heroSection);
    return {
      companyId,
      configId,
      ...heroPayload,
      heroSettingsJson: embedReferenceDisplayInHeroJson(
        String(heroPayload.heroSettingsJson || '{}'),
        settingsToSave.displaySettings
      ),
      itemsPerRow: settingsToSave.displaySettings.itemsPerRow,
      showCompanyName: settingsToSave.displaySettings.showCompanyName,
      showDescription: settingsToSave.displaySettings.showDescription,
      showCategory: settingsToSave.displaySettings.showCategory,
      logoSize: settingsToSave.displaySettings.logoSize,
    };
  };

  // Otomatik kayıt (Debounced)
  const autoSave = async (settingsToSave: ReferencesSettings) => {
    if (!companyId || !configId) {
      console.warn('⚠️ [REFERENCES AUTO-SAVE] CompanyId veya ConfigId yok, kayıt atlanıyor');
      return;
    }

    try {
      console.log('💾 [REFERENCES AUTO-SAVE] Otomatik kaydediliyor...');
      
      const response = await saveReferenceSettings(buildSavePayload(settingsToSave));

      if (response && response.success) {
        // ReferenceConfig ID'yi güncelle
        if (response.data && response.data.id) {
          setReferenceConfigId(response.data.id);
        }
        
        console.log('✅ [REFERENCES AUTO-SAVE] Otomatik kayıt başarılı');
        notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
      }
    } catch (err) {
      console.error('❌ [REFERENCES AUTO-SAVE] Otomatik kayıt hatası:', err);
    }
  };

  // Debounced auto-save
  const debouncedAutoSave = (settingsToSave: ReferencesSettings) => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      autoSave(settingsToSave);
    }, 1000); // 1 saniye bekle
  };

  const updateHeroSection = (heroSection: CorporateHeroSectionSettings) => {
    setSettings((prev) => {
      const newSettings = {
        ...prev,
        heroSection,
      };
      debouncedAutoSave(newSettings);
      if (onSettingsChange) {
        onSettingsChange(newSettings);
      }
      return newSettings;
    });
  };

  // Display settings güncelle (Auto-save ile)
  const updateDisplaySettings = (field: keyof typeof settings.displaySettings, value: any) => {
    const newSettings = {
      ...settings,
      displaySettings: {
        ...settings.displaySettings,
        [field]: value
      }
    };
    setSettings(newSettings);
    
    // Auto-save tetikle
    debouncedAutoSave(newSettings);
    
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Yeni referans ekle
  const handleAddReference = () => {
    const newReference: Reference = {
      id: Date.now().toString(),
      companyName: '',
      category: '',
      active: true
    };
    setEditingReference(newReference);
    setImageFile(null);
    setImagePreview(null);
    setEditDialogOpen(true);
  };

  // ReferenceConfig yoksa oluştur, varsa mevcut ID'yi döndür
  const ensureReferenceConfigId = async (): Promise<string | null> => {
    if (referenceConfigId) return referenceConfigId;
    if (!companyId || !configId) return null;

    try {
      console.log('ℹ️ [REFERENCES] ReferenceConfig bulunamadı, otomatik oluşturuluyor...');
      const response = await saveReferenceSettings(buildSavePayload(settings));

      if (response && response.success && response.data?.id) {
        setReferenceConfigId(response.data.id);
        console.log('✅ [REFERENCES] ReferenceConfig oluşturuldu:', response.data.id);
        return response.data.id;
      }
      return null;
    } catch (err) {
      console.error('❌ [REFERENCES] ReferenceConfig oluşturma hatası:', err);
      return null;
    }
  };

  // Referans düzenle
  const handleEditReference = (reference: Reference) => {
    setEditingReference({ ...reference });
    setImagePreview(reference.logoUrl || null);
    setEditDialogOpen(true);
  };

  // Referans sil (SQL'den)
  const handleDeleteReference = async (referenceId: string) => {
    if (!companyId) { // ✅ companyId null check
      console.warn('⚠️ [REFERENCES] CompanyId yok, silme atlanıyor');
      return;
    }

    try {
      console.log('🗑️ [REFERENCES] Referans siliniyor:', referenceId);
      
      const response = await deleteReferenceItem(referenceId, companyId); // ✅ companyId eklendi
      
      if (response && response.success) {
        // Local state'i güncelle
        const newSettings = {
          ...settings,
          references: settings.references.filter(ref => ref.id !== referenceId)
        };
        setSettings(newSettings);
        
        setSuccess('Referans silindi');
        setTimeout(() => setSuccess(null), 3000);
        
        console.log('✅ [REFERENCES] Referans başarıyla silindi');
      }
    } catch (error) {
      console.error('❌ [REFERENCES] Referans silme hatası:', error);
      setError('An error occurred while deleting reference');
    }
  };

  // Logo yükleme (Image Service - diğer panellerle aynı yöntem)
  const handleImageUpload = async () => {
    if (!imageFile || !companyId) return null;

    try {
      setImageUploading(true);
      console.log('📤 [REFERENCES] Logo Image Service a yükleniyor...');

      const [uploaded] = await ImageUploadService.uploadImages([imageFile]);
      if (!uploaded?.originalUrl) {
        throw new Error('Görsel servisi geçerli bir URL döndürmedi');
      }

      console.log('✅ [REFERENCES] Logo başarıyla yüklendi:', uploaded.originalUrl);
      return uploaded.originalUrl;
    } catch (error: any) {
      console.error('❌ [REFERENCES] Logo yükleme hatası:', error);
      setError(error?.message || 'An error occurred while uploading logo');
      return null;
    } finally {
      setImageUploading(false);
    }
  };

  // Referans kaydet (SQL'e)
  const handleSaveReference = async () => {
    if (!editingReference) {
      setError('Reference info not found');
      return;
    }

    // Config yoksa önce oluştur (ilk kullanımda config kaydı henüz olmayabilir)
    const activeConfigId = await ensureReferenceConfigId();
    if (!activeConfigId) {
      setError('Referans ayarları oluşturulamadı. Lütfen sayfayı yenileyip tekrar deneyin.');
      return;
    }

    try {
      let logoUrl: string | undefined = editingReference.logoUrl;
      
      // Logo yükleme
      if (imageFile) {
        const uploadedUrl = await handleImageUpload();
        if (!uploadedUrl) return;
        logoUrl = uploadedUrl;
      }

      const referenceData = {
        referenceConfigId: activeConfigId,
        companyId: companyId || '',
        companyName: editingReference.companyName,
        logoUrl: logoUrl,
        websiteUrl: editingReference.websiteUrl,
        description: editingReference.description,
        category: editingReference.category,
        displayOrder: settings.references.length
      };

      // Yeni mi yoksa güncelleme mi?
      const isExisting = editingReference.id && settings.references.find(ref => ref.id === editingReference.id);
      
      let response;
      if (isExisting) {
        // Güncelleme
        console.log('🔄 [REFERENCES] Referans güncelleniyor:', editingReference.id);
        response = await updateReferenceItem(editingReference.id, referenceData);
      } else {
        // Yeni ekleme
        console.log('➕ [REFERENCES] Yeni referans ekleniyor');
        response = await addReferenceItem(referenceData);
      }

      if (response && response.success) {
        // Local state'i güncelle
        const updatedReference = { 
          ...editingReference, 
          logoUrl,
          id: response.data?.id || editingReference.id 
        };
        
        const newSettings = {
          ...settings,
          references: isExisting
            ? settings.references.map(ref => ref.id === editingReference.id ? updatedReference : ref)
            : [...settings.references, updatedReference]
        };

        setSettings(newSettings);
        
        setEditDialogOpen(false);
        setEditingReference(null);
        setImageFile(null);
        setImagePreview(null);
      setSuccess(isExisting ? 'Reference updated' : 'Reference added');
        setTimeout(() => setSuccess(null), 3000);
        
        console.log('✅ [REFERENCES] Referans başarıyla kaydedildi');
      } else {
        // Başarısız yanıt sessizce yutulmasın
        console.error('❌ [REFERENCES] Referans kaydetme başarısız:', response);
        setError(response?.message || 'An error occurred while saving reference');
      }
    } catch (error) {
      console.error('❌ [REFERENCES] Referans kaydetme hatası:', error);
      setError('An error occurred while saving reference');
    }
  };

  // Resim dosyası seçimi
  const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size cannot be larger than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          <BusinessIcon sx={{ mr: 1.5, color: 'primary.main' }} />
          {t('ecommerceReferencesSettings.title')}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Hero Section */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceReferencesSettings.sections.hero')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CorporateHeroSettingsPanel
            layoutMode="color-wash"
            value={settings.heroSection}
            onChange={updateHeroSection}
            companyId={companyId}
            configId={configId}
            defaultHeight={400}
            sectionOptions={[
              { label: 'Referanslar', value: 'references-grid' },
              { label: 'SSS', value: 'references_faq' },
            ]}
          />
        </AccordionDetails>
      </Accordion>

      {/* Display Settings */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <StarIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceReferencesSettings.sections.viewSettings')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 700 }}>
                {t('ecommerceReferencesSettings.fields.cardStyle')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {t('ecommerceReferencesSettings.fields.cardStyleHint')}
              </Typography>
              <Grid container spacing={1.25} sx={{ mb: 3 }}>
                {CARD_STYLE_OPTIONS.map((option) => {
                  const selected = settings.displaySettings.cardStyle === option.id;
                  return (
                    <Grid item xs={12} sm={6} key={option.id}>
                      <Box
                        onClick={() => updateDisplaySettings('cardStyle', option.id)}
                        sx={{
                          cursor: 'pointer',
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: selected ? 'primary.main' : 'divider',
                          bgcolor: selected ? 'action.selected' : 'background.paper',
                          p: 1.5,
                          transition: 'all 0.2s ease',
                          '&:hover': { borderColor: 'primary.main' },
                        }}
                      >
                        <Typography variant="body2" fontWeight={700}>
                          {t(`ecommerceReferencesSettings.${option.titleKey}`)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t(`ecommerceReferencesSettings.${option.hintKey}`)}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 700 }}>
                {t('ecommerceReferencesSettings.fields.logoSize')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {LOGO_SIZE_OPTIONS.map((option) => {
                  const selected = settings.displaySettings.logoSize === option.id;
                  return (
                    <Chip
                      key={option.id}
                      label={t(`ecommerceReferencesSettings.${option.titleKey}`)}
                      clickable
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => updateDisplaySettings('logoSize', option.id)}
                    />
                  );
                })}
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 700 }}>
                {t('ecommerceReferencesSettings.fields.logoColor')}
              </Typography>
              <Grid container spacing={1.25} sx={{ mb: 3 }}>
                {LOGO_COLOR_OPTIONS.map((option) => {
                  const selected = settings.displaySettings.logoColor === option.id;
                  return (
                    <Grid item xs={12} sm={6} key={option.id}>
                      <Box
                        onClick={() => updateDisplaySettings('logoColor', option.id)}
                        sx={{
                          cursor: 'pointer',
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: selected ? 'primary.main' : 'divider',
                          p: 1.5,
                          '&:hover': { borderColor: 'primary.main' },
                        }}
                      >
                        <Typography variant="body2" fontWeight={700}>
                          {t(`ecommerceReferencesSettings.${option.titleKey}`)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t(`ecommerceReferencesSettings.${option.hintKey}`)}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              <FormControl fullWidth sx={{ mb: 2.5 }}>
                <InputLabel>{t('ecommerceReferencesSettings.fields.logosPerRow')}</InputLabel>
                <Select
                  value={settings.displaySettings.itemsPerRow}
                  onChange={(e) => updateDisplaySettings('itemsPerRow', Number(e.target.value))}
                  label={t('ecommerceReferencesSettings.fields.logosPerRow')}
                >
                  <MenuItem value={2}>{t('ecommerceReferencesSettings.options.twoColumns')}</MenuItem>
                  <MenuItem value={3}>{t('ecommerceReferencesSettings.options.threeColumns')}</MenuItem>
                  <MenuItem value={4}>{t('ecommerceReferencesSettings.options.fourColumns')}</MenuItem>
                  <MenuItem value={5}>{t('ecommerceReferencesSettings.options.fiveColumns')}</MenuItem>
                  <MenuItem value={6}>{t('ecommerceReferencesSettings.options.sixColumns')}</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.displaySettings.showCompanyName}
                      onChange={(e) => updateDisplaySettings('showCompanyName', e.target.checked)}
                    />
                  }
                  label={t('ecommerceReferencesSettings.fields.showCompanyName')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.displaySettings.showDescription}
                      onChange={(e) => updateDisplaySettings('showDescription', e.target.checked)}
                    />
                  }
                  label={t('ecommerceReferencesSettings.fields.showDescription')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.displaySettings.showCategory}
                      onChange={(e) => updateDisplaySettings('showCategory', e.target.checked)}
                    />
                  }
                  label={t('ecommerceReferencesSettings.fields.showCategoryIndustry')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.displaySettings.showStats}
                      onChange={(e) => updateDisplaySettings('showStats', e.target.checked)}
                    />
                  }
                  label={t('ecommerceReferencesSettings.fields.showStats')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.displaySettings.hoverLift}
                      onChange={(e) => updateDisplaySettings('hoverLift', e.target.checked)}
                    />
                  }
                  label={t('ecommerceReferencesSettings.fields.hoverLift')}
                />
              </Box>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 700 }}>
                {t('ecommerceReferencesSettings.preview.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {t('ecommerceReferencesSettings.preview.hint')}
              </Typography>
              <ReferenceCardPreview
                display={settings.displaySettings}
                sampleName={settings.references[0]?.companyName || t('ecommerceReferencesSettings.preview.sampleName')}
                sampleCategory={settings.references[0]?.category || t('ecommerceReferencesSettings.preview.sampleCategory')}
                sampleLogo={settings.references[0]?.logoUrl}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Referanslar */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceReferencesSettings.references.title', { count: settings.references.length })}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddReference}
            >
              {t('ecommerceReferencesSettings.references.addNew')}
            </Button>
          </Box>

          <Grid container spacing={2}>
            {settings.references.map((reference) => (
              <Grid item xs={12} md={6} lg={4} key={reference.id}>
                <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  {reference.logoUrl && (
                    <Box sx={{ height: 160, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, backgroundColor: '#f8fafc' }}>
                      <img 
                        src={reference.logoUrl} 
                        alt={reference.companyName}
                        style={{ maxWidth: '90%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    </Box>
                  )}
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {reference.companyName || t('ecommerceReferencesSettings.references.companyNameFallback')}
                    </Typography>
                    {reference.category && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {reference.category}
                      </Typography>
                    )}
                    {reference.description && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {reference.description.substring(0, 100)}...
                      </Typography>
                    )}
                    {reference.websiteUrl && (
                      <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
                        {reference.websiteUrl}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions>
                    <IconButton
                      size="small"
                      onClick={() => handleEditReference(reference)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteReference(reference.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Referans Düzenleme Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingReference?.id && settings.references.find(ref => ref.id === editingReference.id)
            ? t('ecommerceReferencesSettings.dialog.editCompanyReference')
            : t('ecommerceReferencesSettings.dialog.addCompanyReference')}
        </DialogTitle>
        <DialogContent>
          {editingReference && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('ecommerceReferencesSettings.dialog.companyNameRequired')}
                  value={editingReference.companyName}
                  onChange={(e) => setEditingReference({ ...editingReference, companyName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('ecommerceReferencesSettings.dialog.categoryIndustry')}
                  value={editingReference.category || ''}
                  onChange={(e) => setEditingReference({ ...editingReference, category: e.target.value })}
                  placeholder={t('ecommerceReferencesSettings.dialog.categoryPlaceholder')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Website URL'si"
                  value={editingReference.websiteUrl || ''}
                  onChange={(e) => setEditingReference({ ...editingReference, websiteUrl: e.target.value })}
                  placeholder="https://www.sirket.com"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label={t('ecommerceAboutSettings.fields.description')}
                  value={editingReference.description || ''}
                  onChange={(e) => setEditingReference({ ...editingReference, description: e.target.value })}
                  placeholder={t('ecommerceReferencesSettings.dialog.shortDescriptionOptional')}
                />
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography gutterBottom>{t('ecommerceReferencesSettings.dialog.companyLogo')}</Typography>
                  <Button variant="outlined" component="label" startIcon={<ImageIcon />}>
                    {t('ecommerceReferencesSettings.dialog.selectLogo')}
                    <input type="file" hidden accept="image/*" onChange={handleImageFileChange} />
                  </Button>
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    {t('ecommerceReferencesSettings.dialog.logoHelp')}
                  </Typography>
                  {imagePreview && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, display: 'inline-block' }}>
                      <img 
                        src={imagePreview} 
                        alt={t('ecommerceReferencesSettings.dialog.logoPreviewAlt')}
                        style={{ maxWidth: '150px', maxHeight: '80px', objectFit: 'contain' }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('ecommerceAboutSettings.actions.cancel')}</Button>
          <Button 
            onClick={handleSaveReference} 
            variant="contained"
            disabled={imageUploading || !editingReference?.companyName}
          >
            {imageUploading ? t('ecommerceReferencesSettings.dialog.uploading') : t('ecommerceReferencesSettings.dialog.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const LOGO_PREVIEW_HEIGHT: Record<ReferenceLogoSize, number> = {
  small: 72,
  medium: 96,
  large: 128,
  xlarge: 168,
};

const ReferenceCardPreview: React.FC<{
  display: ReferenceDisplaySettings;
  sampleName: string;
  sampleCategory: string;
  sampleLogo?: string;
}> = ({ display, sampleName, sampleCategory, sampleLogo }) => {
  const cardSx: Record<ReferenceCardStyle, object> = {
    elevated: {
      bgcolor: '#fff',
      border: '1px solid #eef2f7',
      boxShadow: '0 10px 30px rgba(15,23,42,0.08)',
    },
    outlined: {
      bgcolor: '#fff',
      border: '2px solid #cbd5e1',
    },
    glass: {
      bgcolor: 'rgba(255,255,255,0.75)',
      border: '1px solid rgba(255,255,255,0.9)',
      boxShadow: '0 10px 24px rgba(15,23,42,0.08)',
    },
    soft: {
      bgcolor: '#f8fafc',
      border: '1px solid transparent',
    },
    minimal: {
      bgcolor: 'transparent',
      border: '1px dashed #e2e8f0',
    },
  };

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 3,
        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          ...cardSx[display.cardStyle],
          borderRadius: 3,
          p: 3,
          minHeight: 220,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          transform: display.hoverLift ? 'translateY(-2px)' : 'none',
        }}
      >
        <Box
          sx={{
            height: LOGO_PREVIEW_HEIGHT[display.logoSize],
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: display.showCompanyName || display.showCategory ? 2 : 0,
          }}
        >
          {sampleLogo ? (
            <Box
              component="img"
              src={sampleLogo}
              alt={sampleName}
              sx={{
                maxWidth: '90%',
                maxHeight: '100%',
                objectFit: 'contain',
                filter: display.logoColor === 'grayscale' ? 'grayscale(1)' : 'none',
              }}
            />
          ) : (
            <BusinessIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          )}
        </Box>
        {display.showCompanyName && (
          <Typography variant="subtitle1" fontWeight={700}>
            {sampleName}
          </Typography>
        )}
        {display.showDescription && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Partner açıklaması önizlemesi
          </Typography>
        )}
        {display.showCategory && (
          <Chip size="small" label={sampleCategory} sx={{ mt: 1.5 }} />
        )}
      </Box>
    </Box>
  );
};

export default ReferencesSettingsComponent;
